/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react';
import PropTypes from 'prop-types';

import DisplayPrice from '@salesforce/retail-react-app/app/components/display-price';
import {Box, Radio, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import Swatch from '@salesforce/retail-react-app/app/components/swatch-group/swatch';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';

/**
 * SwatchListRow - A row component for displaying variant swatch in list format
 * Each row contains: thumbnail image, label, price, and radio icon
 */
export function ListItemAttributeValue({
  attribute,
  attributeValue,
  index,
}) {
  const {currency} = useCurrency();

  const {selectedValue} = attribute;
  const {href, image, name, orderable, value, priceData} = attributeValue;

  const hasSelection = Boolean(selectedValue?.value);
  const isSelected = selectedValue?.value === value;
  const isFirst = index === 0;

  // To mimic the behavior of a native radio input, only
  // one swatch should receive tab focus; the rest can be
  // selected using arrow keys when the swatch group has
  // focus. The focused element is the selected option or
  // the first in the group, if no option is selected.
  // This is a slight difference, for simplicity, from the
  // native element, where the first element is focused on
  // `Tab` and the _last_ element is focused on `Shift+Tab`
  const isFocusable = isSelected || (!hasSelection && isFirst);

  const styles = useMultiStyleConfig('SwatchGroup', {
    variant: 'list-item',
    disabled: !orderable,
    selected: isSelected,
  });

  return (
    <Swatch
      key={value}
      disabled={!orderable}
      href={href}
      isFocusable={isFocusable}
      name={name}
      selected={isSelected}
      value={value}
      variant="list-item"
    >
      <Box {...styles.thumbnail}>
        {image ? (
          <Box
            {...styles.thumbnailImage}
            backgroundColor={name.toLowerCase()}
            backgroundImage={`url(${image.disBaseLink || image.link})`}
          />
        ) : (
          <Box {...styles.thumbnailFallback}>
            <Text {...styles.thumbnailFallbackText}>
              {name}
            </Text>
          </Box>
        )}
      </Box>

      <Box {...styles.label}>
        <Text {...styles.labelText}>{name}</Text>
      </Box>

      <Box {...styles.price}>
        {priceData && (
          <DisplayPrice
            currency={currency}
            priceData={priceData}
          />
        )}
      </Box>

      <Box {...styles.radio}>
        <Radio
          inputProps={{'aria-hidden': true, 'tabIndex': -1}}
          isChecked={isSelected}
          isDisabled={!orderable}
        />
      </Box>
    </Swatch>
  );
}

ListItemAttributeValue.displayName = 'ListItemAttributeValue';

ListItemAttributeValue.propTypes = {
  attribute: PropTypes.object.isRequired,
  attributeValue: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};
